<script setup lang="ts">
  import { computed } from 'vue';
  import { IconImage, Tooltip, InfoTooltip } from '@/shared/ui';
  import Trash from '@/shared/assets/icons/TrashIcon.svg';
  import InfoIcon from '@/shared/assets/icons/InfoIcon.svg';
  import type { Position } from '@/entities/Position';
  import { formatMemberAuditDateTime } from '@/pages/admin/lib';

  const props = withDefaults(
    defineProps<{
      position: Position;
      as?: 'tr' | 'div';
    }>(),
    { as: 'tr' }
  );

  const emit = defineEmits<{
    info: [id: number];
    delete: [id: number];
  }>();

  const isDiv = computed(() => props.as === 'div');
  const rowTag = computed(() => (isDiv.value ? 'div' : 'tr'));
  const cellTag = computed(() => (isDiv.value ? 'div' : 'td'));

  const handleInfo = () => {
    emit('info', props.position.id);
  };

  const handleDelete = () => {
    emit('delete', props.position.id);
  };
</script>

<template>
  <component
    :is="rowTag"
    :role="isDiv ? 'row' : undefined"
    class="transition-colors duration-300 ease-out border-b border-gray-02 bg-[rgba(227,227,227,0.30)]"
    :class="{ 'flex items-center': isDiv }"
  >
    <component
      :is="cellTag"
      :role="isDiv ? 'cell' : undefined"
      class="w-20 align-middle text-center"
      :class="{ 'shrink-0': isDiv }"
    />
    <component
      :is="cellTag"
      :role="isDiv ? 'cell' : undefined"
      class="pl-12 py-3 text-sm text-gray-07 text-left"
      :class="{ 'flex-1': isDiv }"
    >
      {{ position.name }}
    </component>
    <component
      :is="cellTag"
      :role="isDiv ? 'cell' : undefined"
      class="pl-6 py-3 text-sm text-gray-07 w-50"
      :class="{ 'shrink-0': isDiv }"
    >
      <div class="flex gap-2 text-gray-xx-02 items-center">
        <span
          class="flex items-center"
          aria-hidden="true"
        >
          <span class="w-6 h-6 inline-block" />
        </span>
        <Tooltip
          content="Deletar"
          placement="top"
          class="flex items-center"
        >
          <button @click="handleDelete">
            <IconImage
              :src="Trash"
              alt="Deletar"
            />
          </button>
        </Tooltip>
        <InfoTooltip
          :icon="InfoIcon"
          title="Informações"
          :created-at="formatMemberAuditDateTime(position.createdAt)"
          :created-by="position.creator?.name"
          :updated-at="formatMemberAuditDateTime(position.updatedAt)"
          :updated-by="position.updater?.name"
          placement="top"
        >
          <button @click="handleInfo">
            <IconImage
              :src="InfoIcon"
              alt="Informações"
            />
          </button>
        </InfoTooltip>
      </div>
    </component>
    <component
      :is="cellTag"
      :role="isDiv ? 'cell' : undefined"
      class="w-12"
      :class="{ 'shrink-0': isDiv }"
    />
  </component>
</template>
